/**
 * sl-53cw.1.1: Unified Legal Consent Component
 *
 * Shared consent UI for ALL flows that require legal acceptance:
 * - Registration: Terms + Privacy (AGB §305 BGB)
 * - Purchase: Terms + Privacy + Withdrawal rights (§312j/§356 BGB)
 *
 * Variant-based rendering: `registration` shows a compact inline checkbox,
 * `purchase` shows the full consent section with withdrawal notice.
 *
 * Uses forwardRef with scrollAndShake() handle for missing consent feedback.
 */

import { useLegalLinks } from '@affine/component';
import { Checkbox } from '@affine/component/ui/checkbox';
import { useI18n } from '@affine/i18n';
import { forwardRef, useImperativeHandle, useRef, useState } from 'react';

import * as styles from './legal-consent-section.css';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type ConsentMode = 'service' | 'digital_content';
export type ConsentVariant = 'registration' | 'purchase';

export interface LegalConsentHandle {
  scrollAndShake: () => void;
}

export interface LegalConsentSectionProps {
  /** Which consent flow: registration (Terms+Privacy) or purchase (Terms+Privacy+Withdrawal) */
  variant: ConsentVariant;
  /** Purchase legal mode: services use Wertersatz, digital content can lose withdrawal right on provision. */
  consentMode?: ConsentMode;
  /** Whether the user has checked the consent checkbox */
  consentGiven: boolean;
  /** Callback when the checkbox state changes */
  onConsentChange: (checked: boolean) => void;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const LegalConsentSection = forwardRef<
  LegalConsentHandle,
  LegalConsentSectionProps
>(function LegalConsentSection(
  { variant, consentMode = 'service', consentGiven, onConsentChange },
  ref
) {
  const t = useI18n();
  const legalLinks = useLegalLinks();
  const sectionRef = useRef<HTMLDivElement>(null);
  const [shaking, setShaking] = useState(false);

  useImperativeHandle(ref, () => ({
    scrollAndShake() {
      sectionRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
      setShaking(true);
      setTimeout(() => setShaking(false), 500);
    },
  }));

  const isPurchase = variant === 'purchase';
  const sectionStyle = isPurchase
    ? styles.legalConsentSection
    : styles.legalConsentSectionInline;

  const consentLabel = isPurchase
    ? consentMode === 'digital_content'
      ? t['com.streamlain.payment.consent.withdrawal-digital-content']()
      : t['com.streamlain.payment.consent.withdrawal-service']()
    : t['com.streamlain.payment.consent.registration']();
  const noticeLinks =
    legalLinks?.terms && legalLinks?.privacy
      ? [
          legalLinks.terms,
          legalLinks.privacy,
          ...(isPurchase && legalLinks.withdrawal
            ? [legalLinks.withdrawal]
            : []),
        ]
      : [];

  return (
    <div
      ref={sectionRef}
      className={`${sectionStyle} ${shaking ? styles.legalConsentShake : ''}`}
    >
      <label className={styles.legalConsentCheckbox}>
        <Checkbox
          checked={consentGiven}
          onChange={(_e: unknown, checked: boolean) => onConsentChange(checked)}
        />
        <span>{consentLabel}</span>
      </label>
      {noticeLinks.length > 0 && (
        <div className={styles.legalNoticeText}>
          {t['com.streamlain.payment.consent.legal-notice-prefix']()}{' '}
          {noticeLinks.map((link, index) => {
            const isLast = index === noticeLinks.length - 1;
            return (
              <span key={link.url}>
                {index > 0
                  ? isLast
                    ? ` ${t['com.streamlain.payment.consent.legal-notice-and']()} `
                    : ', '
                  : ''}
                <a
                  href={link.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={styles.legalLink}
                >
                  {link.label}
                </a>
              </span>
            );
          })}
          .
        </div>
      )}
    </div>
  );
});
